import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useLocation } from '../../contexts/LocationContext';

const MONTH_NAMES_PL = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

/* ---- Styles ---- */

const cardStyleFn = (color) => ({
  backgroundColor: 'var(--card-bg)',
  borderLeft: `3px solid ${color}`,
  borderRadius: '4px',
  padding: '0.5rem 0.75rem',
  textAlign: 'center',
  minWidth: '90px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
});

const valueStyle = {
  fontSize: '16px',
  fontWeight: '700',
  color: 'var(--text)',
};

const labelStyle = {
  fontSize: '10px',
  color: 'var(--text-muted)',
  marginTop: '2px',
};

const popupStyle = {
  position: 'absolute',
  top: '100%',
  left: '0',
  marginTop: '6px',
  width: '280px',
  backgroundColor: 'var(--card-bg, #fff)',
  border: '1px solid var(--border, #e5e7eb)',
  borderRadius: '8px',
  padding: '12px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  zIndex: 1000,
};

const rowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '4px',
};

/* ---- Component ---- */

export default function BonusWidget({ refreshTrigger }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const { locationId } = useLocation();
  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  const fetchBonus = useCallback(async () => {
    try {
      const params = locationId ? { location_id: locationId } : {};
      const res = await api.get('/bonus/my', { params });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    fetchBonus();
  }, [fetchBonus, refreshTrigger]);

  // Close popup on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (loading) {
    return (
      <div style={cardStyleFn('#f59e0b')}>
        <div style={valueStyle}>...</div>
        <div style={labelStyle}>Premia mies.</div>
      </div>
    );
  }

  if (!data || !data.data) return null;

  // Obsługa odpowiedzi z API - może być obiekt lub struktura z lokalizacjami
  const d = data.data || {};
  const bonusData = d.lokalizacje && d.lokalizacje.length > 0 ? d.lokalizacje[0] : d;

  const premiaRazem = d.premia_razem ?? bonusData.premia_razem ?? 0;
  const sprzedaz = bonusData.sprzedaz_ogolem ?? 0;
  const progEfektywny = bonusData.prog_efektywny ?? 0;
  const nastepnyProg = bonusData.nastepny_prog;
  const progressTarget = nastepnyProg ? nastepnyProg.kwota : progEfektywny;
  const progressPct = progressTarget > 0 ? Math.min((sprzedaz / progressTarget) * 100, 100) : 0;
  const monthName = MONTH_NAMES_PL[new Date().getMonth()];

  const szczegoly = bonusData.szczegoly || {};
  const premiaBazowa = bonusData.premia_bazowa ?? 0;
  const premiaProduktowa = bonusData.premia_produktowa ?? 0;
  const produkty = szczegoly.produkty || [];
  const wyzwaniaArr = Array.isArray(bonusData.wyzwania) ? bonusData.wyzwania : [];
  const wyzwaniaUkonczone = wyzwaniaArr.filter((w) => w.ukonczony).length;
  const wyzwaniaTotal = wyzwaniaArr.length;

  const barColor = premiaRazem > 0 ? '#22c55e' : '#f59e0b';

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      {/* Main card */}
      <div
        style={{ ...cardStyleFn(barColor), cursor: 'pointer' }}
        onClick={() => setOpen((v) => !v)}
      >
        <div style={valueStyle}>🏆 {premiaRazem.toFixed(2)} zł</div>
        <div style={labelStyle}>Premia mies.</div>
        {/* Tiny progress bar */}
        <div
          style={{
            marginTop: '4px',
            height: '3px',
            backgroundColor: 'var(--border, #e5e7eb)',
            borderRadius: '2px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${progressPct}%`,
              height: '100%',
              backgroundColor: barColor,
              borderRadius: '2px',
              transition: 'width 0.4s ease',
            }}
          />
        </div>
      </div>

      {/* Popup */}
      {open && (
        <div style={popupStyle}>
          <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px', color: 'var(--text)' }}>
            Szczegóły premii — {monthName}
          </div>

          {/* Sales progress */}
          <div style={rowStyle}>
            <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Sprzedaż:</span>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>
              {sprzedaz.toFixed(2)} / {progressTarget.toFixed(2)} zł
            </span>
          </div>

          {/* Progress bar */}
          <div
            style={{
              height: '6px',
              backgroundColor: 'var(--border, #e5e7eb)',
              borderRadius: '3px',
              overflow: 'hidden',
              margin: '4px 0',
            }}
          >
            <div
              style={{
                width: `${progressPct}%`,
                height: '100%',
                backgroundColor: barColor,
                borderRadius: '3px',
                transition: 'width 0.4s ease',
              }}
            />
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', textAlign: 'right', marginBottom: '6px' }}>
            {progressPct.toFixed(0)}%
          </div>

          {/* Next threshold hint */}
          {nastepnyProg && (
            <div style={{ fontSize: '11px', color: '#f59e0b', marginBottom: '6px' }}>
              → jeszcze {(nastepnyProg.brakuje ?? 0).toFixed(2)} zł do +{nastepnyProg.premia.toFixed(2)} zł premii!
            </div>
          )}

          {/* Breakdown */}
          <div style={rowStyle}>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Premia bazowa:</span>
            <span style={{ fontSize: '12px', color: 'var(--text)' }}>
              {premiaBazowa.toFixed(2)} zł {premiaBazowa > 0 ? '✅' : '❌'}
            </span>
          </div>
          <div style={rowStyle}>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Premia produktowa:</span>
            <span style={{ fontSize: '12px', color: 'var(--text)' }}>
              {premiaProduktowa.toFixed(2)} zł
            </span>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--border, #e5e7eb)', margin: '8px 0' }} />

          <div style={{ ...rowStyle, fontWeight: 700 }}>
            <span style={{ fontSize: '13px', color: 'var(--text)' }}>RAZEM:</span>
            <span style={{ fontSize: '13px', color: 'var(--text)' }}>{premiaRazem.toFixed(2)} zł</span>
          </div>

          {/* Top premium products */}
          {produkty.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                Ostatnie produkty premiowe:
              </div>
              {produkty.slice(0, 3).map((p, i) => (
                <div key={i} style={{ fontSize: '11px', color: 'var(--text)', lineHeight: '1.5' }}>
                  ✅ {p.nazwa} (+{(p.premia ?? 0).toFixed(2)} zł)
                </div>
              ))}
            </div>
          )}

          {/* Challenges summary */}
          {wyzwaniaTotal > 0 && (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
              Wyzwania: {wyzwaniaUkonczone}/{wyzwaniaTotal} ukończone
            </div>
          )}

          {/* Link to full view */}
          <div
            style={{
              marginTop: '10px',
              fontSize: '11px',
              color: '#3b82f6',
              cursor: 'pointer',
              textAlign: 'right',
            }}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              navigate('/bonuses/my');
            }}
          >
            Więcej szczegółów →
          </div>
        </div>
      )}
    </div>
  );
}
